<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useAccount } from "dashboard/composables/useAccount";
import AnnouncementModal from "./AnnouncementModal.vue";
import AnnouncementBanner from "./AnnouncementBanner.vue";

const store = useStore();
const { accountId } = useAccount();

const pending = computed(() => store.getters["announcements/getPending"]);
const noticePending = computed(() =>
  pending.value.filter((a) => a.level === "notice"),
);
const warningPending = computed(() =>
  pending.value.filter((a) => a.level === "warning"),
);

// No interrumpir a un agente que está tipeando (regla de negocio): si hay algo
// para mostrar mientras el foco está en un input/textarea/contenteditable
// (incluye el editor ProseMirror del reply box), se difiere hasta que salga
// de ahí, no hasta que se cierre el propio modal/banner.
const isTypingElement = (el) => {
  if (!el) return false;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") return true;
  return !!el.isContentEditable;
};

const deferredUntilBlur = ref(false);

const onFocusOut = () => {
  requestAnimationFrame(() => {
    if (!isTypingElement(document.activeElement)) {
      deferredUntilBlur.value = false;
    }
  });
};

onMounted(() => {
  document.addEventListener("focusout", onFocusOut);
});
onBeforeUnmount(() => {
  document.removeEventListener("focusout", onFocusOut);
});

watch(
  [noticePending, warningPending],
  ([notice, warning]) => {
    if (
      (notice.length || warning.length) &&
      isTypingElement(document.activeElement)
    ) {
      deferredUntilBlur.value = true;
    }
  },
  { immediate: true },
);

const showModal = computed(
  () => noticePending.value.length > 0 && !deferredUntilBlur.value,
);
const showBanner = computed(
  () =>
    !showModal.value &&
    warningPending.value.length > 0 &&
    !deferredUntilBlur.value,
);

// Se dispara también desde Sidebar.vue en su propio onMounted: acá se repite
// a propósito para no depender de qué componente monta primero.
watch(
  accountId,
  (newAccountId) => {
    if (newAccountId) store.dispatch("announcements/fetchPending");
  },
  { immediate: true },
);
</script>

<template>
  <AnnouncementModal v-if="showModal" :announcements="noticePending" />
  <AnnouncementBanner
    v-else-if="showBanner"
    :announcement="warningPending[0]"
  />
</template>
